'use client';

import { useEffect, useRef } from 'react';
import { useSite } from 'src/context/providers/SiteProvider';
import { ListActionTypes } from 'src/context/reducers/actionTypes';
import { useParams } from 'next/navigation';
import { useGetListByIdQuery } from 'src/api/redux/lists/listsApi';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import {
  selectSelectedList,
  setSelectedList as setReduxSelectedList,
} from 'src/redux/slices/uiPreferencesSlice';

interface ListOwner {
  id: number;
  first_name: string;
  last_name: string;
}

interface ListOption {
  id: string;
  name: string;
  path: string;
  ai_details?: Record<string, unknown>;
  type?: string;
  num_items?: number;
  owner?: ListOwner;
}

interface ListQueryParams {
  [key: string]: string | number | boolean;
}

const DEFAULT_OWNER: ListOwner = {
  id: 0,
  first_name: '',
  last_name: '',
};

const createBasicListFromUrl = (
  listId: string,
  listPath: string,
  existingList?: ListOption
): ListOption => ({
  id: listId,
  path: listPath,
  name: existingList?.name || '',
  owner: existingList?.owner || DEFAULT_OWNER,
});

const useLists = () => {
  const { state, dispatch } = useSite();
  const { selectedList } = state.lists;
  const storedList = useAppSelector(selectSelectedList);
  const reduxDispatch = useAppDispatch();
  const setStoredList = (value: any) =>
    reduxDispatch(setReduxSelectedList(value));
  const { view, id } = useParams<{ view: string; id: string[] }>();
  const isDropdownSelection = useRef(false);
  const isInitialMount = useRef(true);

  const { data: listData } = useGetListByIdQuery(
    { listId: selectedList?.id },
    { skip: !selectedList?.id }
  );

  // Handle URL and localStorage hydration
  useEffect(() => {
    if (isDropdownSelection.current) {
      isDropdownSelection.current = false;
      return;
    }

    if (view && id && Array.isArray(id)) {
      const listId = id[0];
      const listPath = `/lists/${view}/${listId}`;

      if (!selectedList || selectedList.id !== listId) {
        const storedListMatch = storedList?.id === listId ? storedList : null;
        const listToSet =
          storedListMatch ||
          createBasicListFromUrl(listId, listPath, selectedList);

        dispatch({
          type: ListActionTypes.SET_SELECTED_LIST,
          payload: listToSet,
        });

        if (!storedListMatch) {
          setStoredList(listToSet);
        }
      }
    } else if (
      storedList &&
      (!selectedList || selectedList.id !== storedList.id) &&
      isInitialMount.current
    ) {
      dispatch({
        type: ListActionTypes.SET_SELECTED_LIST,
        payload: storedList,
      });
    }

    isInitialMount.current = false;
  }, [view, id, dispatch, selectedList, storedList]);

  // Update list data when API data becomes available
  useEffect(() => {
    if (!listData || !selectedList) return;

    if (
      selectedList.name === listData.name &&
      selectedList.owner?.id === listData.owner.id
    ) {
      return;
    }

    const updatedList: ListOption = {
      ...selectedList,
      id: listData.id.toString(),
      name: listData.name,
      ai_details: listData.ai_details,
      type: listData.type,
      num_items: listData.num_items,
      owner: {
        id: listData.owner.id,
        first_name: listData.owner.first_name,
        last_name: listData.owner.last_name,
      },
    };

    dispatch({
      type: ListActionTypes.SET_SELECTED_LIST,
      payload: updatedList,
    });
    setStoredList(updatedList);
  }, [listData, selectedList, dispatch, setStoredList]);

  const setSelectedList = (list: ListOption) => {
    isDropdownSelection.current = true;
    dispatch({ type: ListActionTypes.SET_SELECTED_LIST, payload: list });
    setStoredList(list);
  };

  const setListQueryParams = (params: ListQueryParams) => {
    dispatch({ type: ListActionTypes.SET_LIST_QUERY_PARAMS, payload: params });
  };

  const setShowReassignListDialog = (show: boolean) => {
    dispatch({
      type: ListActionTypes.SET_SHOW_REASSIGN_LIST_DIALOG,
      payload: show,
    });
  };

  const setShowShareListDialog = (show: boolean) => {
    dispatch({
      type: ListActionTypes.SET_SHOW_SHARE_LIST_DIALOG,
      payload: show,
    });
  };

  const setShowDeleteListDialog = (show: boolean) => {
    dispatch({
      type: ListActionTypes.SET_SHOW_DELETE_LIST_DIALOG,
      payload: show,
    });
  };

  const setListToManage = (listId: string) => {
    dispatch({
      type: ListActionTypes.SET_LIST_TO_MANAGE,
      payload: listId,
    });
  };

  const triggerListMembersRefetch = () => {
    dispatch({
      type: ListActionTypes.TRIGGER_LIST_MEMBERS_REFETCH,
    });
  };

  return {
    ...state.lists,
    storedList,
    setListQueryParams,
    setSelectedList,
    setShowReassignListDialog,
    setShowShareListDialog,
    setShowDeleteListDialog,
    setListToManage,
    triggerListMembersRefetch,
  };
};

export default useLists;
